import { useState } from 'react';
import {
  Database,
  Calendar,
  Clock,
  HardDrive,
  User,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Info,
} from 'lucide-react';

import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

import {
  getCollectionMeta,
  groupCollectionsByCategory,
  CATEGORY_CONFIG,
} from './backupCollectionMeta';

const BackupDetailsModal = ({ backup, onClose, onRestore }) => {
  const [expandedCategories, setExpandedCategories] = useState(new Set(['Website Content']));

  const allCollections = backup?.collections || [];
  const groupedCollections = groupCollectionsByCategory(allCollections);

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Expand all categories
  const expandAll = () => {
    setExpandedCategories(new Set(groupedCollections.map((g) => g.category)));
  };

  // Collapse all categories
  const collapseAll = () => {
    setExpandedCategories(new Set());
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  };

  // Format bytes
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Format duration
  const formatDuration = (start, end) => {
    if (!start || !end) return '-';
    const duration = new Date(end) - new Date(start);
    const seconds = Math.floor(duration / 1000);
    if (seconds < 60) return `${seconds} seconds`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Get trigger label and color
  const getTriggerInfo = (trigger) => {
    const info = {
      scheduled: { label: 'Scheduled', color: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300', desc: 'Daily automated backup' },
      manual: { label: 'Manual', color: 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300', desc: 'Triggered by admin' },
      pre_restore: { label: 'Pre-Restore', color: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300', desc: 'Auto-created before restore' },
      restore: { label: 'Restore', color: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300', desc: 'Restore operation record' },
    };
    return info[trigger] || { label: trigger, color: 'bg-stone-100 dark:bg-stone-700 text-stone-800 dark:text-stone-200', desc: 'Unknown trigger' };
  };

  const triggerInfo = getTriggerInfo(backup.trigger);
  const totalDocs = allCollections.reduce((sum, c) => sum + (c.count || 0), 0);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Database className="h-5 w-5" />
            Backup Details
          </DialogTitle>
          <DialogDescription>
            Detailed information about this database backup
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Backup Summary */}
          <div className="grid grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-stone-400 mt-0.5" />
                <div>
                  <p className="text-xs text-stone-500 dark:text-stone-400 uppercase tracking-wide">Created</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100 dark:text-gray-100">{formatTimestamp(backup.started_at)}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-stone-400 mt-0.5" />
                <div>
                  <p className="text-xs text-stone-500 dark:text-stone-400 uppercase tracking-wide">Duration</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100 dark:text-gray-100">
                    {formatDuration(backup.started_at, backup.completed_at)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-stone-400 mt-0.5" />
                <div>
                  <p className="text-xs text-stone-500 dark:text-stone-400 uppercase tracking-wide">Triggered By</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100 dark:text-gray-100">{backup.triggered_by || 'system'}</p>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <HardDrive className="h-5 w-5 text-stone-400 mt-0.5" />
                <div>
                  <p className="text-xs text-stone-500 dark:text-stone-400 uppercase tracking-wide">Total Size</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100 dark:text-gray-100">
                    {formatBytes(backup.total_size_bytes)} (compressed)
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Database className="h-5 w-5 text-stone-400 mt-0.5" />
                <div>
                  <p className="text-xs text-stone-500 dark:text-stone-400 uppercase tracking-wide">Collections</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100 dark:text-gray-100">
                    {allCollections.length} collections ({totalDocs.toLocaleString()} documents)
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                {backup.status === 'completed' ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                )}
                <div>
                  <p className="text-xs text-stone-500 dark:text-stone-400 uppercase tracking-wide">Status</p>
                  <div className="flex items-center gap-2">
                    <Badge variant={backup.status === 'completed' ? 'success' : 'destructive'}>
                      {backup.status}
                    </Badge>
                    <Badge variant="outline" className={triggerInfo.color}>
                      {triggerInfo.label}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error message if failed */}
          {backup.error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm font-medium text-red-800 dark:text-red-300">Error Message</p>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">{backup.error}</p>
            </div>
          )}

          {/* Collections by Category */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 dark:text-gray-100">Collections in Backup</h4>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={expandAll}>
                  Expand All
                </Button>
                <Button variant="ghost" size="sm" onClick={collapseAll}>
                  Collapse All
                </Button>
              </div>
            </div>

            <div className="border border-stone-200 dark:border-stone-700 rounded-lg overflow-hidden">
              {groupedCollections.map((group) => {
                const isExpanded = expandedCategories.has(group.category);
                const categoryDocs = group.collections.reduce((sum, c) => sum + (c.count || 0), 0);
                const categorySize = group.collections.reduce((sum, c) => sum + (c.size_bytes || 0), 0);

                return (
                  <Collapsible
                    key={group.category}
                    open={isExpanded}
                    onOpenChange={() => toggleCategory(group.category)}
                  >
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between p-3 bg-stone-50 dark:bg-stone-800 border-b border-stone-200 dark:border-stone-700 hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors cursor-pointer">
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-stone-500" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-stone-500" />
                          )}
                          <Badge variant="outline" className={group.config.color}>
                            {group.category}
                          </Badge>
                          <span className="text-sm text-stone-600 dark:text-stone-300">
                            {group.collections.length} collection{group.collections.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-stone-500 dark:text-stone-400">
                          <span>{categoryDocs.toLocaleString()} docs</span>
                          <span>{formatBytes(categorySize)}</span>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="divide-y divide-stone-100 dark:divide-stone-700">
                        {group.collections.map((collection) => {
                          const meta = getCollectionMeta(collection.name);
                          return (
                            <div
                              key={collection.name}
                              className="flex items-center justify-between p-3 pl-10 hover:bg-stone-50 dark:hover:bg-stone-800"
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="font-mono text-sm text-gray-900 dark:text-gray-100 dark:text-gray-100 truncate">
                                  {collection.name}
                                </span>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="h-3.5 w-3.5 text-stone-400 flex-shrink-0 cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      <p>{meta.description}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <div className="flex items-center gap-4 text-xs text-stone-500 dark:text-stone-400 flex-shrink-0">
                                <span>{(collection.count || 0).toLocaleString()} docs</span>
                                <span className="w-16 text-right">{formatBytes(collection.size_bytes)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          </div>

          {/* Category Legend */}
          <div className="p-4 bg-stone-50 dark:bg-stone-800 rounded-lg border border-stone-200 dark:border-stone-700">
            <p className="text-xs font-medium text-stone-600 dark:text-stone-300 uppercase tracking-wide mb-3">
              Category Guide
            </p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(CATEGORY_CONFIG)
                .sort((a, b) => a[1].order - b[1].order)
                .map(([category, config]) => (
                  <div key={category} className="flex items-start gap-2">
                    <Badge variant="outline" className={`${config.color} text-xs flex-shrink-0`}>
                      {category}
                    </Badge>
                    <span className="text-xs text-stone-600 dark:text-stone-400">{config.description}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {backup.status === 'completed' && onRestore && (
            <Button
              onClick={() => onRestore(backup)}
              className="bg-gradient-to-br from-red-800 to-red-900 hover:from-amber-600 hover:to-amber-700"
            >
              <Database className="h-4 w-4 mr-2" />
              Restore from Backup
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BackupDetailsModal;
